// =============================================================================
// hooks/dashboard/useDashboardMetrics.ts — TanStack Query para GET /api/dashboard/metrics.
//
// - Query key inclui range + cityId + datas customizadas.
// - staleTime 60 s + refetch a cada 90 s com a aba visível.
//
//   Era 3 min de staleTime SEM refetchInterval. O staleTime suprime até o
//   refetch-on-focus do TanStack, então a matriz — que deixa a tela aberta o dia
//   inteiro — via dados de vários minutos atrás sem nenhum indício disso, e
//   concluía "o dashboard não atualiza" (diagnóstico D4). O hook devolve
//   `updatedAt`/`isFetching` justamente para a tela poder dizer a verdade.
//
// - Nunca usa useEffect + fetch — só TanStack Query sobre lib/api.ts.
// =============================================================================

import { useQuery } from '@tanstack/react-query';

import { ApiError, api } from '../../lib/api';

import type { DashboardMetricsQuery, DashboardMetricsResponse } from './types';

// ---------------------------------------------------------------------------
// Query key factory — centraliza a forma da key para invalidação
// ---------------------------------------------------------------------------

export const dashboardKeys = {
  all: ['dashboard'] as const,
  metrics: (query: DashboardMetricsQuery) => [...dashboardKeys.all, 'metrics', query] as const,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseDashboardMetricsResult {
  data: DashboardMetricsResponse | undefined;
  isLoading: boolean;
  /** true durante revalidação em background (com dado antigo ainda na tela). */
  isFetching: boolean;
  isError: boolean;
  isForbidden: boolean;
  error: Error | null;
  /** Instante do último dado recebido — alimenta o "Atualizado às HH:MM". */
  updatedAt: number;
  refetch: () => void;
}

/**
 * Hook canônico para métricas do dashboard.
 * Consome GET /api/dashboard/metrics com range e cityId opcionais.
 * Cache de 3 min (staleTime). Refetch automático ao mudar params.
 */
export function useDashboardMetrics(query: DashboardMetricsQuery = {}): UseDashboardMetricsResult {
  const { range = 'last30d', cityId, dateFrom, dateTo } = query;

  const params = new URLSearchParams();
  params.set('range', range);
  if (cityId) params.set('cityId', cityId);
  if (range === 'custom' && dateFrom) params.set('dateFrom', dateFrom);
  if (range === 'custom' && dateTo) params.set('dateTo', dateTo);

  // Build query key with only defined values (exactOptionalPropertyTypes)
  const queryKeyParams: DashboardMetricsQuery = {
    range,
    ...(cityId ? { cityId } : {}),
    ...(range === 'custom' && dateFrom ? { dateFrom } : {}),
    ...(range === 'custom' && dateTo ? { dateTo } : {}),
  };

  const { data, isLoading, isFetching, isError, error, dataUpdatedAt, refetch } = useQuery<
    DashboardMetricsResponse,
    Error
  >({
    queryKey: dashboardKeys.metrics(queryKeyParams),
    queryFn: () => api.get<DashboardMetricsResponse>(`/api/dashboard/metrics?${params.toString()}`),
    staleTime: 60 * 1000,
    refetchInterval: 90 * 1000,
    // Aba escondida não gasta request nem bateria: quem está com a tela em
    // segundo plano não está lendo o número.
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: (failureCount, err) => {
      // Não retenta 403 (sem permissão) nem 404
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) return false;
      return failureCount < 2;
    },
  });

  const isForbidden = isError && error instanceof ApiError && error.status === 403;

  return {
    data,
    isLoading,
    isFetching,
    isError,
    isForbidden,
    error,
    updatedAt: dataUpdatedAt,
    refetch,
  };
}
